import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
    useMsal,
    useIsAuthenticated
} from "@azure/msal-react";

import {
    InteractionStatus,
    InteractionRequiredAuthError,
    AccountInfo
} from "@azure/msal-browser";

import MenuIcon from "@mui/icons-material/Menu";

import {
    AppBar,
    Box,
    Container,
    Grid,
    IconButton,
    Link,
    Menu,
    MenuItem,
    Toolbar,
    Alert
} from "@mui/material";

import {
    QueryClient,
    QueryClientProvider
} from "@tanstack/react-query";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import AvatarMenu from "./AvatarMenu";
import SignInSignOutButton from "./LoginButtons";

import {
    forecastServerLoginRequest,
    graphLoginRequest
} from "../util/AuthConfig";

import {
    GraphService,
    IProfile
} from "../util/GraphService";

import {
    ImpersonatedUserContext
} from "../contexts/ImpersonatedUserContext";

import {
    GraphProfileContext
} from "../contexts/GraphProfileContext";

import {
    IExtendedEmployee
} from "../models/IEmployee";

import useFetchWithMsal from "../hooks/useFetchWithMsal";

import VirtualizedSelector from "./VirtualizedSelector";

import {
    ISignedInUser
} from "../models/ISignedInUser";


const queryClient = new QueryClient();


interface IPages {
    children: React.ReactNode;
}


// =============================================================
// FREEZE API RESPONSE INTERFACES
// =============================================================

interface IFreezeDateRange {

    startDateTime: string | null;

    endDateTime: string | null;

    IsUpcoming?: boolean;

    Message?: string;

    isUpcoming?: boolean;

    message?: string;
}


interface IActiveFreezeResponse {

    IsActive: boolean;

    EndDateTime: string | null;

    Message?: string;

    isActive?: boolean;

    endDateTime?: string | null;

    message?: string;
}


// =============================================================
// PAGE LAYOUT
// =============================================================

const PageLayout: React.FunctionComponent<any> = (
    props: IPages
) => {

    const { children } = props;


    // =========================================================
    // MSAL
    // =========================================================

    const {
        inProgress,
        instance,
        accounts
    } = useMsal();


    const isAuthenticated =
        useIsAuthenticated();


    // =========================================================
    // GENERAL STATE
    // =========================================================

    const [graphProfile, setGraphProfile] =
        useState<IProfile>({
            photo: undefined,
            displayName: "unknown",
            email: "unknown"
        });


    const [impersonatedUser, setImpersonatedUser] =
        useState<IExtendedEmployee | null>(null);


    const [navAnchor, setNavAnchor] =
        useState<null | HTMLElement>(null);


    const [loaded, setLoaded] =
        useState(false);


    const [signedInUserData, setSignedInUserData] =
        useState<ISignedInUser | null>(null);


    // =========================================================
    // FREEZE STATE
    // =========================================================

    const [reminderStart, setReminderStart] =
        useState<Date | null>(null);


    const [freezeEnd, setFreezeEnd] =
        useState<Date | null>(null);


    const [upcomingMessage, setUpcomingMessage] =
        useState<string>("");


    const [activeMessage, setActiveMessage] =
        useState<string>("");


    // API FLAGS
    const [upcomingFromApi, setUpcomingFromApi] =
        useState<boolean>(false);


    const [activeFromApi, setActiveFromApi] =
        useState<boolean>(false);


    const open =
        Boolean(navAnchor);


    // =========================================================
    // API SERVICE
    // =========================================================

    const { execute } =
        useFetchWithMsal({
            scopes:
                forecastServerLoginRequest.scopes
        });


    // =========================================================
    // LOAD FREEZE INFORMATION
    // =========================================================

    useEffect(() => {

        const loadFreezeDates = async () => {

            try {

                // =================================================
                // WAIT FOR AUTHENTICATION
                // =================================================

                if (
                    inProgress !==
                        InteractionStatus.None ||
                    !isAuthenticated
                ) {

                    return;

                }


                console.log(
                    "Loading freeze information from backend..."
                );


                // =================================================
                // API 1
                //
                // GET UPCOMING FREEZE / REMINDER
                // =================================================

                const freezeDateRange =
                    await execute<IFreezeDateRange>(
                        "GET",
                        "/api/Freeze/GetDateBeforeFreezeStartAsync"
                    );


                console.log(
                    "Upcoming Freeze API Response:",
                    freezeDateRange
                );


                // =================================================
                // READ API 1 RESPONSE
                // =================================================

                const isUpcoming =
                    Boolean(
                        freezeDateRange?.IsUpcoming ??
                        freezeDateRange?.isUpcoming ??
                        false
                    );


                const upcomingMsg =
                    freezeDateRange?.Message ??
                    freezeDateRange?.message ??
                    "";


                const reminderStartRaw =
                    freezeDateRange?.startDateTime ??
                    null;


                const upcomingEndRaw =
                    freezeDateRange?.endDateTime ??
                    null;


                // =================================================
                // SET UPCOMING FLAG
                // =================================================

                setUpcomingFromApi(
                    isUpcoming
                );


                // =================================================
                // SET UPCOMING MESSAGE
                // =================================================

                setUpcomingMessage(
                    upcomingMsg
                );


                // =================================================
                // REMINDER START DATE
                //
                // This is only stored for information/fallback.
                //
                // IMPORTANT:
                // We DO NOT calculate freeze start by adding one day.
                // =================================================

                if (reminderStartRaw) {

                    const reminderDate =
                        new Date(reminderStartRaw);


                    console.log(
                        "Reminder Start:",
                        reminderDate
                    );


                    setReminderStart(
                        reminderDate
                    );

                }
                else {

                    setReminderStart(
                        null
                    );

                }


                // =================================================
                // FREEZE END DATE
                // =================================================

                if (upcomingEndRaw) {

                    const endDate =
                        new Date(upcomingEndRaw);


                    console.log(
                        "Freeze End from Upcoming API:",
                        endDate
                    );


                    setFreezeEnd(
                        endDate
                    );

                }


                // =================================================
                // API 2
                //
                // GET CURRENT ACTIVE FREEZE
                // =================================================

                const currentFreeze =
                    await execute<IActiveFreezeResponse>(
                        "GET",
                        "/api/Freeze/GetCurrentFreezeStartDateAsync"
                    );


                console.log(
                    "Current Freeze API Response:",
                    currentFreeze
                );


                // =================================================
                // READ API 2 RESPONSE
                // =================================================

                const isActive =
                    Boolean(
                        currentFreeze?.IsActive ??
                        currentFreeze?.isActive ??
                        false
                    );


                const activeMsg =
                    currentFreeze?.Message ??
                    currentFreeze?.message ??
                    "";


                const activeEndRaw =
                    currentFreeze?.EndDateTime ??
                    currentFreeze?.endDateTime ??
                    null;


                // =================================================
                // SET ACTIVE FLAG
                // =================================================

                setActiveFromApi(
                    isActive
                );


                // =================================================
                // SET ACTIVE MESSAGE
                // =================================================

                setActiveMessage(
                    activeMsg
                );


                // =================================================
                // ACTIVE FREEZE END DATE
                // =================================================

                if (activeEndRaw) {

                    const activeEndDate =
                        new Date(activeEndRaw);


                    console.log(
                        "Active Freeze End:",
                        activeEndDate
                    );


                    setFreezeEnd(
                        activeEndDate
                    );

                }

            }
            catch (error) {

                console.error(
                    "Error while loading freeze information:",
                    error
                );


                // =================================================
                // RESET FREEZE STATE
                // =================================================

                setReminderStart(null);

                setFreezeEnd(null);

                setUpcomingMessage("");

                setActiveMessage("");

                setUpcomingFromApi(false);

                setActiveFromApi(false);

            }

        };


        loadFreezeDates();

    }, [
        execute,
        inProgress,
        isAuthenticated
    ]);


    // =========================================================
    // BANNER VISIBILITY
    // =========================================================
    //
    // BACKEND IS THE SOURCE OF TRUTH
    //
    // API 1:
    // isUpcoming = true
    //      => Reminder Banner
    //
    // API 2:
    // isActive = true
    //      => Active Freeze Banner
    //
    // Active Freeze always has priority.
    // =========================================================

    const showFreezeBanner =
        activeFromApi;


    const displayReminder =
        upcomingFromApi &&
        !activeFromApi;


    // =========================================================
    // DATE FORMATTER
    // =========================================================

    const formatDateTime = (
        date: Date | null
    ): string => {

        if (!date) {

            return "";

        }


        return date.toLocaleString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
            }
        );

    };


    const formattedFreezeEnd =
        formatDateTime(
            freezeEnd
        );


    // =========================================================
    // DEBUG LOG
    // =========================================================

    console.log(
        "Freeze Banner Information:",
        {
            reminderStart,
            freezeEnd,
            upcomingFromApi,
            activeFromApi,
            upcomingMessage,
            activeMessage,
            displayReminder,
            showFreezeBanner
        }
    );


    // =========================================================
    // MENU HANDLERS
    // =========================================================

    const handleMenuClick = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {

        setNavAnchor(
            event.currentTarget
        );

    };


    const handleClose = () => {

        setNavAnchor(
            null
        );

    };


    // =========================================================
    // IMPERSONATED USER REF
    // =========================================================

    const impersonatedUserIdRef =
        React.useRef<
            string | null | undefined
        >(null);


    impersonatedUserIdRef.current =
        impersonatedUser?.id ?? null;


    // =========================================================
    // LOAD PROFILE DATA
    // =========================================================

    const setProfileData =
        useCallback(
            async () => {

                try {

                    if (
                        inProgress ===
                            InteractionStatus.None &&
                        isAuthenticated &&
                        instance
                    ) {

                        console.log(
                            "Querying and setting profile data..."
                        );


                        // =================================================
                        // GRAPH PROFILE
                        // =================================================

                        const prof =
                            await GraphService.GetProfile(
                                instance,
                                accounts
                            );


                        const signedInUserEmail:
                            string | undefined =
                            instance
                                .getActiveAccount()
                                ?.username;


                        // =================================================
                        // LOAD SIGNED-IN USER SETTINGS
                        // =================================================

                        const signedInUserSettings:
                            ISignedInUser | null =
                            (await execute(
                                "POST",
                                "/api/Home/LoadUserSettings",
                                signedInUserEmail
                            )) as ISignedInUser | null;


                        console.info(
                            "Using WD Env: " +
                            (
                                signedInUserSettings
                                    ?.workdayEnvironment ??
                                "Not Sandbox"
                            )
                        );


                        // =================================================
                        // SYNC IMPERSONATED USER
                        // =================================================

                        const sessionImpersonatedUser:
                            IExtendedEmployee | null =
                            (await execute(
                                "POST",
                                "/api/Home/SyncImpersonatedUser",
                                {
                                    SignedInUserEmail:
                                        signedInUserEmail,

                                    ImpersonatedUserId:
                                        impersonatedUserIdRef
                                            .current ??
                                        null
                                }
                            )) as IExtendedEmployee | null;


                        // =================================================
                        // UPDATE STATE
                        // =================================================

                        setImpersonatedUser(
                            sessionImpersonatedUser
                        );


                        setSignedInUserData(
                            signedInUserSettings
                        );


                        setGraphProfile(
                            prof
                        );


                        setLoaded(
                            true
                        );

                    }

                }
                catch (e) {

                    if (
                        e instanceof
                        InteractionRequiredAuthError
                    ) {

                        instance.acquireTokenRedirect({

                            ...graphLoginRequest,

                            account:
                                instance
                                    .getActiveAccount() as AccountInfo

                        });

                    }


                    console.log(e);

                }

            },
            [
                accounts,
                instance,
                execute,
                isAuthenticated,
                inProgress
            ]
        );


    // =========================================================
    // LOAD PROFILE ON LOGIN
    // =========================================================

    useEffect(() => {

        const getProfileDataAsync =
            async () => {

                if (
                    !graphProfile.photo &&
                    inProgress ===
                        InteractionStatus.None
                ) {

                    try {

                        await setProfileData();

                    }
                    catch (err) {

                        console.table(
                            err
                        );

                    }

                }

            };


        getProfileDataAsync();

    }, [
        inProgress,
        graphProfile,
        setProfileData
    ]);


    // =========================================================
    // UI
    // =========================================================

    return (

        <>

            {/* =====================================================
                AUTHENTICATED USER
            ===================================================== */}

            <AuthenticatedTemplate>

                <QueryClientProvider
                    client={queryClient}
                >

                    <GraphProfileContext.Provider
                        value={graphProfile}
                    >

                        <ImpersonatedUserContext.Provider
                            value={{
                                impersonatedUser,
                                setImpersonatedUser
                            }}
                        >

                            {/* =================================================
                                NAVIGATION BAR
                            ================================================= */}

                            <AppBar
                                position="sticky"
                                color="primary"
                            >

                                <Toolbar
                                    sx={{
                                        backgroundColor:
                                            "#6AA2DC"
                                    }}
                                >

                                    {/* MENU BUTTON */}

                                    <IconButton
                                        size="large"
                                        edge="start"
                                        color="inherit"
                                        aria-label="open drawer"
                                        sx={{
                                            mr: 2
                                        }}
                                        onClick={
                                            handleMenuClick
                                        }
                                    >

                                        <MenuIcon />

                                    </IconButton>


                                    {/* NAVIGATION MENU */}

                                    <Menu
                                        id="nav-menu"
                                        anchorEl={
                                            navAnchor
                                        }
                                        open={
                                            open
                                        }
                                        onClose={
                                            handleClose
                                        }
                                        MenuListProps={{
                                            "aria-labelledby":
                                                "basic-button"
                                        }}
                                    >

                                        <MenuItem
                                            onClick={
                                                handleClose
                                            }
                                        >

                                            <Link
                                                to="/"
                                                component={
                                                    RouterLink
                                                }
                                                underline="none"
                                            >

                                                Home

                                            </Link>

                                        </MenuItem>


                                        <MenuItem
                                            onClick={
                                                handleClose
                                            }
                                            disabled={
                                                !signedInUserData
                                                    ?.hasAccessSecurity
                                            }
                                        >

                                            <Link
                                                to="/security"
                                                component={
                                                    RouterLink
                                                }
                                                underline="none"
                                            >

                                                Security

                                            </Link>

                                        </MenuItem>


                                        <MenuItem
                                            onClick={
                                                handleClose
                                            }
                                            disabled={
                                                !signedInUserData
                                                    ?.hasAccessFreezeScheduler
                                            }
                                        >

                                            <Link
                                                to="/freeze"
                                                component={
                                                    RouterLink
                                                }
                                                underline="none"
                                            >

                                                Freeze Scheduler

                                            </Link>

                                        </MenuItem>

                                    </Menu>


                                    {/* APPLICATION TITLE */}

                                    <Link
                                        sx={{
                                            flexGrow: 1,
                                            textAlign:
                                                "center",
                                            width:
                                                1 / 2
                                        }}
                                        component={
                                            RouterLink
                                        }
                                        to="/"
                                        underline="none"
                                        color="inherit"
                                        variant="h4"
                                    >

                                        Cognizant Forecast Tracker{" "}

                                        {
                                            signedInUserData
                                                ?.workdayEnvironment ??
                                            ""
                                        }

                                    </Link>


                                    {/* IMPERSONATION SELECTOR */}

                                    <VirtualizedSelector
                                        options={
                                            signedInUserData
                                                ?.subEmployeeList ??
                                            []
                                        }
                                    />


                                    {/* USER PROFILE */}

                                    <AvatarMenu />

                                </Toolbar>


                                {/* =================================================
                                    FORECAST FREEZE REMINDER
                                ================================================= */}

                                {displayReminder && (

                                    <Box
                                        sx={{
                                            width: "100%",
                                            backgroundColor:
                                                "#FFF3CD",
                                            display: "flex",
                                            justifyContent:
                                                "center"
                                        }}
                                    >

                                        <Alert
                                            severity="warning"
                                            icon={false}
                                            sx={{
                                                width: "100%",
                                                maxWidth:
                                                    "1100px",
                                                borderRadius:
                                                    0,
                                                backgroundColor:
                                                    "#FFF3CD",
                                                color:
                                                    "#664D03",
                                                py: 0.75,
                                                px: 2,
                                                boxShadow:
                                                    "none",
                                                display: "flex",
                                                justifyContent:
                                                    "center",
                                                alignItems:
                                                    "center",
                                                fontSize:
                                                    "14px",

                                                "& .MuiAlert-message":
                                                {
                                                    padding: 0,
                                                    width: "100%",
                                                    textAlign:
                                                        "center",
                                                    whiteSpace:
                                                    {
                                                        xs:
                                                            "normal",
                                                        md:
                                                            "nowrap"
                                                    }
                                                }
                                            }}
                                        >

                                            {upcomingMessage ? (

                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            upcomingMessage
                                                    }}
                                                />

                                            ) : (

                                                <>
                                                    <strong>
                                                        Forecast Freeze Reminder:
                                                    </strong>

                                                    {" "}

                                                    Forecast freeze
                                                    is upcoming.
                                                    Changes will not
                                                    sync to Workday.

                                                </>

                                            )}

                                        </Alert>

                                    </Box>

                                )}


                                {/* =================================================
                                    ACTIVE FORECAST FREEZE
                                ================================================= */}

                                {showFreezeBanner && (

                                    <Box
                                        sx={{
                                            width: "100%",
                                            backgroundColor:
                                                "#F8D7DA",
                                            display: "flex",
                                            justifyContent:
                                                "center"
                                        }}
                                    >

                                        <Alert
                                            severity="error"
                                            icon={false}
                                            sx={{
                                                width: "100%",
                                                maxWidth:
                                                    "1100px",
                                                borderRadius:
                                                    0,
                                                backgroundColor:
                                                    "#F8D7DA",
                                                color:
                                                    "#842029",
                                                py: 0.75,
                                                px: 2,
                                                boxShadow:
                                                    "none",
                                                display: "flex",
                                                justifyContent:
                                                    "center",
                                                alignItems:
                                                    "center",
                                                fontSize:
                                                    "14px",

                                                "& .MuiAlert-message":
                                                {
                                                    padding: 0,
                                                    width: "100%",
                                                    textAlign:
                                                        "center",
                                                    whiteSpace:
                                                    {
                                                        xs:
                                                            "normal",
                                                        md:
                                                            "nowrap"
                                                    }
                                                }
                                            }}
                                        >

                                            {activeMessage ? (

                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            activeMessage
                                                    }}
                                                />

                                            ) : (

                                                <>
                                                    <strong>
                                                        Forecast Active Freeze:
                                                    </strong>

                                                    {" "}

                                                    Portal is currently
                                                    frozen. Changes will
                                                    not sync to Workday

                                                    {formattedFreezeEnd && (

                                                        <>
                                                            {" "}
                                                            until{" "}

                                                            <strong>
                                                                {formattedFreezeEnd}
                                                            </strong>
                                                        </>

                                                    )}

                                                    .

                                                </>

                                            )}

                                        </Alert>

                                    </Box>

                                )}

                            </AppBar>


                            {/* =================================================
                                MAIN CONTENT
                            ================================================= */}

                            <main>

                                <Container
                                    maxWidth={false}
                                    id="containerChildren"
                                >

                                    <Container
                                        maxWidth={false}
                                        id="containerVirtualizerEmpty"
                                        sx={{
                                            paddingTop:
                                                "1.5%"
                                        }}
                                    />

                                    {loaded &&
                                        children}

                                </Container>

                            </main>

                        </ImpersonatedUserContext.Provider>

                    </GraphProfileContext.Provider>


                    {/* =================================================
                        REACT QUERY DEVTOOLS
                    ================================================= */}

                    <ReactQueryDevtools
                        initialIsOpen={true}
                    />


                    {/* =================================================
                        TOAST CONTAINER
                    ================================================= */}

                    <ToastContainer
                        position="top-right"
                        autoClose={8000}
                        newestOnTop
                        closeOnClick
                    />

                </QueryClientProvider>

            </AuthenticatedTemplate>


            {/* =====================================================
                NOT AUTHENTICATED
            ===================================================== */}

            <UnauthenticatedTemplate>

                <Box>

                    <Grid
                        container
                        justifyContent="center"
                    >

                        <h1>
                            Not Authenticated
                        </h1>

                    </Grid>


                    <Grid
                        container
                        justifyContent="center"
                    >

                        <SignInSignOutButton />

                    </Grid>

                </Box>

            </UnauthenticatedTemplate>

        </>

    );

};


export default PageLayout;

